/**
 * @file
 * Opinion actions bar: modal bottom sheets for page contents and share links.
 *
 * The sheets dim the page, so they are dialogs, not disclosures: Escape from
 * anywhere, focus kept inside, focus returned on close, page held still.
 */

((Drupal, once) => {
  const DESKTOP_QUERY = '(min-width: 900px)';
  const BODY_FLAG = 'has-opinion-actions-bar';
  const LOCK_FLAG = 'has-opinion-sheet-open';

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  /**
   * Lists the focusable elements inside a container, skipping hidden ones.
   */
  const focusableWithin = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (element) => element.offsetParent !== null || element === document.activeElement,
    );

  /**
   * Gives an element focus without leaving it in the tab order.
   */
  const focusTemporarily = (element) => {
    if (!element) {
      return;
    }
    if (!element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '-1');
      element.addEventListener('blur', () => element.removeAttribute('tabindex'), {
        once: true,
      });
    }
    element.focus({ preventScroll: true });
  };

  /**
   * Collects the page's major sections for the "On this page" sheet.
   *
   * Twig carries the label on data-opinion-section, so the copy stays
   * translatable. A section with no id has nothing to link to, so it is skipped.
   */
  const collectSections = () =>
    Array.from(document.querySelectorAll('[data-opinion-section]'))
      .filter((section) => section.id)
      .map((section) => ({
        id: section.id,
        label: section.getAttribute('data-opinion-section').trim(),
        target: section,
      }))
      .filter((section) => section.label);

  // Jump links are wired separately from the bar so the desktop card keeps its
  // own focus handling, which leaves them with no way to reach the bar's
  // closeAll. Each bar publishes one here for them to call.
  const sheetClosers = new Set();

  /**
   * Wires one actions bar.
   */
  const initializeBar = (bar) => {
    const cleanup = [];

    // Rendered in node content to share the tray's data, but a fixed element
    // clipped by an ancestor's containing block loses its right edge. Reparent
    // to <body>; the flag is set only after the move, so a script failure
    // leaves the bar visible rather than hidden.
    if (bar.parentElement !== document.body) {
      const placeholder = document.createComment('opinion-actions-bar');
      bar.parentNode.insertBefore(placeholder, bar);
      document.body.appendChild(bar);
      document.body.classList.add(BODY_FLAG);
      cleanup.push(() => {
        document.body.classList.remove(BODY_FLAG);
        if (placeholder.parentNode) {
          placeholder.parentNode.insertBefore(bar, placeholder);
          placeholder.remove();
        }
      });
    }

    const desktop = window.matchMedia(DESKTOP_QUERY);
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    let disclosures = Array.from(bar.querySelectorAll('[aria-controls]'))
      .map((button) => {
        const sheet = bar.querySelector(`#${CSS.escape(button.getAttribute('aria-controls'))}`);
        return sheet ? { button, sheet } : null;
      })
      .filter(Boolean);

    const anyOpen = () =>
      disclosures.some((d) => d.button.getAttribute('aria-expanded') === 'true');

    // One owner for scrim and scroll lock, so they cannot disagree.
    const syncOpenState = () => {
      const open = anyOpen();
      bar.classList.toggle('is-sheet-open', open);
      document.documentElement.classList.toggle(LOCK_FLAG, open);
    };
    cleanup.push(() => document.documentElement.classList.remove(LOCK_FLAG));

    const setExpanded = (disclosure, open) => {
      const { button, sheet } = disclosure;
      button.setAttribute('aria-expanded', String(open));
      if (open) {
        sheet.removeAttribute('hidden');
        sheet.removeAttribute('inert');
      } else {
        if (sheet.contains(document.activeElement)) {
          button.focus();
        }
        sheet.setAttribute('hidden', '');
        sheet.setAttribute('inert', '');
      }
      syncOpenState();
    };

    const closeAll = (except) => {
      disclosures.forEach((disclosure) => {
        if (disclosure !== except) {
          setExpanded(disclosure, false);
        }
      });
    };

    const openDisclosure = () =>
      disclosures.find((d) => d.button.getAttribute('aria-expanded') === 'true');

    disclosures.forEach((disclosure) => {
      setExpanded(disclosure, false);

      const onClick = () => {
        const open = disclosure.button.getAttribute('aria-expanded') === 'true';
        closeAll(disclosure);
        setExpanded(disclosure, !open);
        if (!open) {
          // Focus the dialog, not its first link: a scripted focus() on an
          // anchor matches :focus-visible and paints the global white ring on
          // what was only a tap.
          focusTemporarily(disclosure.sheet);
        }
      };
      disclosure.button.addEventListener('click', onClick);
      cleanup.push(() => disclosure.button.removeEventListener('click', onClick));
    });

    // On the document, not the bar: with the scrim up the whole viewport
    // belongs to the sheet, and a user who tabbed out needs a way back.
    const onKeydown = (event) => {
      const open = openDisclosure();
      if (!open) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        setExpanded(open, false);
        open.button.focus();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      // Trigger plus sheet form the dialog; Tab cycles inside it.
      const stops = [open.button, ...focusableWithin(open.sheet)];
      if (stops.length === 0) {
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;

      if (!open.sheet.contains(active) && active !== open.button) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeydown);
    cleanup.push(() => document.removeEventListener('keydown', onKeydown));

    // Arrow keys, Home and End move between the bar's controls, on top of Tab
    // rather than instead of it, so every control stays in the tab order.
    // Ignored while a sheet is open: the dialog owns focus then.
    const list = bar.querySelector('.bottom-tab-bar__list');
    const onArrowKey = (event) => {
      if (openDisclosure() || !event.target.matches('.bottom-tab-bar__control')) {
        return;
      }
      const controls = Array.from(
        list.querySelectorAll(':scope > .bottom-tab-bar__item > .bottom-tab-bar__control'),
      ).filter((control) => !control.closest('[hidden]'));
      const index = controls.indexOf(event.target);
      if (index === -1) {
        return;
      }

      let next;
      if (event.key === 'ArrowRight') {
        next = controls[(index + 1) % controls.length];
      } else if (event.key === 'ArrowLeft') {
        next = controls[(index - 1 + controls.length) % controls.length];
      } else if (event.key === 'Home') {
        [next] = controls;
      } else if (event.key === 'End') {
        next = controls[controls.length - 1];
      } else {
        return;
      }
      event.preventDefault();
      next.focus();
    };
    if (list) {
      list.addEventListener('keydown', onArrowKey);
      cleanup.push(() => list.removeEventListener('keydown', onArrowKey));
    }

    // The bar paints the scrim, so a scrim click reports the bar as target.
    // Test the interactive subtrees, not the host.
    const onDocumentClick = (event) => {
      const open = openDisclosure();
      if (!open) {
        return;
      }
      if (!event.target.closest('.bottom-tab-bar__list, .bottom-tab-bar__sheet')) {
        // A scrim tap leaves focus on <body>, so setExpanded's handover never
        // fires. Hand it back the way Escape does.
        setExpanded(open, false);
        open.button.focus();
        closeAll(null);
      }
    };
    document.addEventListener('click', onDocumentClick);
    cleanup.push(() => document.removeEventListener('click', onDocumentClick));

    sheetClosers.add(closeAll);
    cleanup.push(() => sheetClosers.delete(closeAll));

    // Move focus to the jump target rather than only scrolling to it.
    const jumpTo = (target) => {
      closeAll(null);
      focusTemporarily(target);
      target.scrollIntoView({
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });
    };

    const contentsItem = bar.querySelector('[data-opinion-contents-item]');
    const contentsList = bar.querySelector('[data-opinion-contents-list]');

    if (contentsItem && contentsList) {
      const sections = collectSections();
      if (sections.length > 1) {
        sections.forEach((section) => {
          const listItem = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'bottom-tab-bar__sheet-link';
          link.href = `#${section.id}`;
          link.textContent = section.label;

          const onJump = (event) => {
            event.preventDefault();
            // preventDefault would lose the hash; keep it shareable.
            if (window.history && window.history.pushState) {
              window.history.pushState(null, '', `#${section.id}`);
            }
            jumpTo(section.target);
          };
          link.addEventListener('click', onJump);
          cleanup.push(() => link.removeEventListener('click', onJump));

          listItem.appendChild(link);
          contentsList.appendChild(listItem);
        });
        contentsItem.hidden = false;
      } else {
        // One section is not worth a jump list. Drop its disclosure too, or
        // closeAll keeps walking a detached subtree.
        const contentsButton = contentsItem.querySelector('[aria-controls]');
        disclosures = disclosures.filter((d) => d.button !== contentsButton);
        contentsItem.remove();
      }
    }

    const applyViewportMode = () => {
      if (desktop.matches) {
        closeAll(null);
      }
    };
    applyViewportMode();
    if (typeof desktop.addEventListener === 'function') {
      desktop.addEventListener('change', applyViewportMode);
      cleanup.push(() => desktop.removeEventListener('change', applyViewportMode));
    } else if (typeof desktop.addListener === 'function') {
      desktop.addListener(applyViewportMode);
      cleanup.push(() => desktop.removeListener(applyViewportMode));
    }

    bar.amacoeOpinionActionsCleanup = () => {
      cleanup.forEach((callback) => callback());
      delete bar.amacoeOpinionActionsCleanup;
    };
  };

  /**
   * Wires the jump links, which also exist on the desktop card.
   *
   * Separate from the bar so the card keeps focus handling on wide screens,
   * where no bar is rendered.
   */
  const initializeJumpLink = (link) => {
    link.addEventListener('click', (event) => {
      const id = link.getAttribute('href').slice(1);
      const target = document.getElementById(id);
      if (!target) {
        return;
      }
      event.preventDefault();
      // Usable while a sheet is open; leaving it open would strand the scrim
      // and scroll lock over a page the user has scrolled away from.
      sheetClosers.forEach((closeAll) => closeAll(null));
      // Keep the hash, as the Contents links do.
      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', `#${id}`);
      }
      focusTemporarily(target);
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth',
        block: 'start',
      });
    });
  };

  if (Drupal && once) {
    /**
     * @type {Drupal~behavior}
     */
    Drupal.behaviors.amacoeOpinionActions = {
      attach(context) {
        once('amacoe-opinion-actions', '[data-opinion-actions-bar]', context).forEach(
          initializeBar,
        );
        once('amacoe-opinion-jump', '[data-opinion-jump]', context).forEach(
          initializeJumpLink,
        );
      },

      detach(context, settings, trigger) {
        if (trigger !== 'unload') {
          return;
        }
        // The bar lives on <body>, so a scoped context never finds it and the
        // document listeners would outlive the page. But tearing down on every
        // unload would kill it for good. Only act when the whole document goes.
        if (context !== document && context !== document.body) {
          return;
        }
        once
          .remove('amacoe-opinion-actions', '[data-opinion-actions-bar]', document.body)
          .forEach((bar) => bar.amacoeOpinionActionsCleanup?.());
      },
    };
  } else {
    const attachStandalone = () => {
      document.querySelectorAll('[data-opinion-actions-bar]').forEach((bar) => {
        if (!bar.dataset.amacoeOpinionActions) {
          bar.dataset.amacoeOpinionActions = 'true';
          initializeBar(bar);
        }
      });
      document.querySelectorAll('[data-opinion-jump]').forEach((link) => {
        if (!link.dataset.amacoeOpinionJump) {
          link.dataset.amacoeOpinionJump = 'true';
          initializeJumpLink(link);
        }
      });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachStandalone);
    } else {
      attachStandalone();
    }
  }
})(window.Drupal, window.once);
